import { useCallback, useEffect, useRef, useState } from 'react';
import {
  applyDefaultWidths, clampPaneWidth, clampSidebarWidth, loadLayout, resizePaneBy, saveLayout,
  setDataPane as setDataPanePure, setMainWidth, setPaneWidth, setSatelliteWidth, showPane, togglePane,
  type DataPaneId, type PaneId, type PaneLayout,
} from './paneLayout';

/**
 * 右侧两槽位布局的状态容器：读写 + 持久化 + 恢复默认。
 *
 * 所有状态变换都委托给 `paneLayout.ts` 里的**纯函数**（可单测），
 * 这里只负责 setState 与持久化。
 *
 * 持久化策略：拖拽期间会以每帧一次的频率 setState，因此写 localStorage 做 250ms 防抖，
 * 避免每一帧都 `JSON.stringify` 一遍。卸载时再补一次落盘，保证"拖完立刻离开创作页"也不丢。
 */
export interface PaneLayoutApi {
  layout: PaneLayout;
  setSidebarWidth: (px: number) => void;
  /** 按**增量**调整侧栏宽度（拖拽必须走这个：用闭包里的旧值会丢增量） */
  resizeSidebarBy: (delta: number) => void;
  /** 功能区宽度（绝对 / 增量）。两个槽位**共用同一个值**，拖拽走增量版。 */
  setPaneWidth: (px: number) => void;
  resizePaneBy: (delta: number) => void;
  /** 点图标栏：打开 / 收起（两个槽位互不影响 —— AI 在左、数据页在右） */
  toggle: (id: PaneId) => void;
  /** 直接设置右侧数据页（吸附窗口关闭/换页、全局搜索跳转用它） */
  setDataPane: (pane: DataPaneId | null) => void;
  /** 把某页显示出来（全局搜索跳转；**不**关掉另一页） */
  show: (id: PaneId) => void;
  /** 记下主窗体宽度（用户拖窗口后写回，下次打开沿用） */
  rememberMainWidth: (px: number) => void;
  /** 记住副窗口宽度（它自报宽度时用） */
  rememberSatelliteWidth: (px: number) => void;
  /** 按屏幕宽度重算三项默认宽度（首次启动 / 设置页重置） */
  applyWidths: (screenWidth: number, tierBasisWidth?: number) => void;
}

const PERSIST_DEBOUNCE_MS = 250;

export function usePaneLayout(): PaneLayoutApi {
  const [layout, setLayout] = useState<PaneLayout>(() => loadLayout());

  // 始终指向最新布局，供卸载补写与防抖回调（避免闭包旧值）
  const latest = useRef(layout);
  latest.current = layout;

  useEffect(() => {
    const timer = window.setTimeout(() => saveLayout(latest.current), PERSIST_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [layout]);

  useEffect(() => () => { saveLayout(latest.current); }, []);

  const setSidebarWidth = useCallback((px: number) => {
    setLayout(prev => ({ ...prev, sidebar: clampSidebarWidth(px) }));
  }, []);

  const resizeSidebarBy = useCallback((delta: number) => {
    setLayout(prev => ({ ...prev, sidebar: clampSidebarWidth(prev.sidebar + delta) }));
  }, []);

  const setPaneWidthValue = useCallback((px: number) => {
    setLayout(prev => setPaneWidth(prev, clampPaneWidth(px)));
  }, []);

  const resizePaneByValue = useCallback((delta: number) => {
    setLayout(prev => resizePaneBy(prev, delta));
  }, []);

  const toggle = useCallback((id: PaneId) => { setLayout(prev => togglePane(prev, id)); }, []);
  const show = useCallback((id: PaneId) => { setLayout(prev => showPane(prev, id)); }, []);
  const setDataPane = useCallback((pane: DataPaneId | null) => {
    setLayout(prev => setDataPanePure(prev, pane));
  }, []);
  const rememberMainWidth = useCallback((px: number) => { setLayout(prev => setMainWidth(prev, px)); }, []);
  const rememberSatelliteWidth = useCallback((px: number) => { setLayout(prev => setSatelliteWidth(prev, px)); }, []);
  const applyWidths = useCallback((screenWidth: number, tierBasisWidth?: number) => {
    setLayout(prev => applyDefaultWidths(prev, screenWidth, tierBasisWidth));
  }, []);

  return {
    layout,
    setSidebarWidth, resizeSidebarBy,
    setPaneWidth: setPaneWidthValue, resizePaneBy: resizePaneByValue,
    toggle, show, setDataPane,
    rememberMainWidth, rememberSatelliteWidth, applyWidths,
  };
}
